import { useState, useEffect, useMemo, useCallback } from 'react';
import { MapMeta } from '../../types/map';
import { Asset } from '../../types/asset';
import { assetsApi, mapsApi, settingsApi, ApiError, GlobalSettings } from '../../services/api';
import { useEditor } from '../../contexts/EditorContext';
import Checkbox from '../../components/Checkbox';
import Toggle from '../../components/Toggle';
import './GeneralSettingsTab.css';

interface GeneralSettingsTabProps {
  map: MapMeta;
  onMapUpdate: (updatedMap: MapMeta) => void;
}

/**
 * Extracts all unique folder paths from maps
 */
function extractFolderPaths(maps: { folder?: string }[]): string[] {
  const folders = new Set<string>();
  for (const mapItem of maps) {
    if (mapItem.folder) {
      const parts = mapItem.folder.split('/');
      let current = '';
      for (const part of parts) {
        current = current ? `${current}/${part}` : part;
        folders.add(current);
      }
    }
  }
  return Array.from(folders).sort();
}

/**
 * General settings tab for map configuration
 * Includes label editing, background material selection, and background options
 */
const DEFAULT_SCALE = 128;

function GeneralSettingsTab({ map, onMapUpdate }: GeneralSettingsTabProps): JSX.Element {
  const { maps } = useEditor();
  const [label, setLabel] = useState<string>(map.label);
  const [scaleInput, setScaleInput] = useState<string>((map.scale ?? DEFAULT_SCALE).toString());
  const [folder, setFolder] = useState<string>(map.folder ?? '');

  // Parse scale from input string
  const scale = parseFloat(scaleInput) || 0;
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [isCreatingFolder, setIsCreatingFolder] = useState<boolean>(false);
  const [backgroundMaterial, setBackgroundMaterial] = useState<string | null>(
    map.backgroundMaterial ?? null
  );
  const [backgroundColor, setBackgroundColor] = useState<string | null>(
    map.backgroundColor ?? null
  );
  const [backgroundRandomRotation, setBackgroundRandomRotation] = useState<boolean>(
    map.backgroundRandomRotation ?? false
  );
  const [isPublic, setIsPublic] = useState<boolean>(map.isPublic ?? false);

  // Track if custom color is selected (vs material or none)
  const isCustomColorSelected = backgroundColor !== null && backgroundMaterial === null;
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings | null>(null);
  const [materials, setMaterials] = useState<Asset[]>([]);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string>('');

  const availableFolders = useMemo(() => extractFolderPaths(maps), [maps]);

  // Fetch materials for background selection (global assets)
  useEffect(() => {
    const fetchMaterials = async (): Promise<void> => {
      setIsLoadingMaterials(true);
      try {
        const response = await assetsApi.list('materials');
        setMaterials(response.assets);
      } catch (err) {
        console.error('Failed to load materials:', err);
      } finally {
        setIsLoadingMaterials(false);
      }
    };

    fetchMaterials();
  }, []);

  // Fetch global settings
  useEffect(() => {
    settingsApi.get()
      .then(res => setGlobalSettings(res.settings))
      .catch(console.error);
  }, []);

  /**
   * Handle toggling default map
   */
  const handleDefaultMapToggle = useCallback(async (isDefault: boolean): Promise<void> => {
    try {
      const newMapId = isDefault ? map.id : null;
      const res = await settingsApi.setDefaultMap(newMapId);
      setGlobalSettings(res.settings);
    } catch (err) {
      console.error('Failed to update default map:', err);
    }
  }, [map.id]);

  // Reset local state when map changes
  useEffect(() => {
    setLabel(map.label);
    setScaleInput((map.scale ?? DEFAULT_SCALE).toString());
    setFolder(map.folder ?? '');
    setBackgroundMaterial(map.backgroundMaterial ?? null);
    setBackgroundColor(map.backgroundColor ?? null);
    setBackgroundRandomRotation(map.backgroundRandomRotation ?? false);
    setIsPublic(map.isPublic ?? false);
  }, [map]);

  /**
   * Saves the current settings to the server
   */
  const handleSave = async (): Promise<void> => {
    // Validate scale before saving
    if (scale < 0.00001 || scale > 1024) {
      setSaveError('Scale must be between 0.00001 and 1024');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const response = await mapsApi.updateSettings(map.id, {
        label,
        scale,
        folder: folder || undefined,
        backgroundMaterial,
        backgroundColor,
        backgroundRandomRotation,
        isPublic
      });
      onMapUpdate(response.meta);
    } catch (err) {
      if (err instanceof ApiError) {
        setSaveError(err.message);
      } else {
        setSaveError('Failed to save settings');
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handles adding a new folder
   */
  const handleAddFolder = (): void => {
    if (!newFolderName.trim()) return;
    const sanitized = newFolderName.trim().replace(/[^\w\s\u00C0-\u024F-]/g, '');
    if (!sanitized) return;
    const newPath = folder ? `${folder}/${sanitized}` : sanitized;
    setFolder(newPath);
    setNewFolderName('');
    setIsCreatingFolder(false);
  };

  /**
   * Handles background material selection
   */
  const handleBackgroundSelect = (materialName: string | null): void => {
    setBackgroundMaterial(materialName);
    // Clear custom color when selecting a material
    if (materialName !== null) {
      setBackgroundColor(null);
    }
  };

  /**
   * Handles custom color selection
   */
  const handleCustomColorSelect = (): void => {
    setBackgroundMaterial(null);
    // Set default color if none exists
    if (!backgroundColor) {
      setBackgroundColor('#1a1a1a');
    }
  };

  /**
   * Validates and sets hex color
   */
  const handleColorChange = (value: string): void => {
    // Allow editing - just store the raw value
    const cleaned = value.startsWith('#') ? value : `#${value}`;
    setBackgroundColor(cleaned.slice(0, 7));
  };

  // Check if there are unsaved changes
  const hasChanges =
    label !== map.label ||
    scale !== (map.scale ?? DEFAULT_SCALE) ||
    folder !== (map.folder ?? '') ||
    backgroundMaterial !== (map.backgroundMaterial ?? null) ||
    backgroundColor !== (map.backgroundColor ?? null) ||
    backgroundRandomRotation !== (map.backgroundRandomRotation ?? false) ||
    isPublic !== (map.isPublic ?? false);

  return (
    <div className="general-settings-tab">
      <div className="general-settings-section">
        <div className="general-settings-section-title">Map Information</div>
        <div className="general-settings-field">
          <label>Label</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Map Label"
          />
        </div>
        <div className="general-settings-field">
          <label>ID</label>
          <input
            type="text"
            value={map.id}
            disabled
            className="general-settings-field-disabled"
          />
          <span className="general-settings-field-hint">Map ID cannot be changed</span>
        </div>
        <div className="general-settings-field">
          <label>Scale (pixels per meter)</label>
          <input
            type="text"
            inputMode="decimal"
            placeholder="128"
            value={scaleInput}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                setScaleInput(value);
              }
            }}
          />
          <span className="general-settings-field-hint">
            {scale > 0 && scale >= 1
              ? `1m = ${scale}px. Grid squares and scale bar will adjust accordingly.`
              : scale > 0
                ? `1px = ${(1 / scale).toLocaleString()}m. For planetary/world maps.`
                : 'Enter a scale value'
            }
          </span>
        </div>
        <div className="general-settings-field">
          <label>Folder</label>
          <div className="general-settings-folder-row">
            <select
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
            >
              <option value="">Root (no folder)</option>
              {availableFolders.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
              {folder && !availableFolders.includes(folder) && (
                <option value={folder}>{folder} (new)</option>
              )}
            </select>
            <button
              type="button"
              className="general-settings-folder-add"
              onClick={() => setIsCreatingFolder(true)}
              title="Create new folder"
            >
              <i className="fas fa-folder-plus"></i>
            </button>
          </div>
          {isCreatingFolder && (
            <div className="general-settings-new-folder">
              <input
                type="text"
                placeholder="New folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddFolder();
                  } else if (e.key === 'Escape') {
                    setIsCreatingFolder(false);
                    setNewFolderName('');
                  }
                }}
                autoFocus
              />
              <button type="button" onClick={handleAddFolder}>
                <i className="fas fa-check"></i>
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsCreatingFolder(false);
                  setNewFolderName('');
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
          <span className="general-settings-field-hint">
            Organize maps into folders for easier navigation
          </span>
        </div>
      </div>

      <div className="general-settings-section">
        <div className="general-settings-section-title">Background</div>

        <div className="general-settings-subsection">
          <div className="general-settings-subsection-title">Background Material</div>
          {isLoadingMaterials ? (
            <div className="general-settings-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading materials...</span>
            </div>
          ) : materials.length === 0 ? (
            <div className="general-settings-empty">
              No materials available. Add materials in the Materials tab.
            </div>
          ) : (
            <>
              <div className="general-settings-materials-grid">
                <button
                  className={`general-settings-material-item ${backgroundMaterial === null && backgroundColor === null ? 'general-settings-material-item--selected' : ''}`}
                  onClick={() => {
                    setBackgroundMaterial(null);
                    setBackgroundColor(null);
                  }}
                  title="No background (default gray)"
                >
                  <div className="general-settings-material-preview general-settings-material-none">
                    <i className="fas fa-ban"></i>
                  </div>
                  <span>None</span>
                </button>
                <button
                  className={`general-settings-material-item ${isCustomColorSelected ? 'general-settings-material-item--selected' : ''}`}
                  onClick={handleCustomColorSelect}
                  title="Custom color"
                >
                  <div className="general-settings-material-preview general-settings-material-color">
                    <div
                      className="general-settings-material-color-swatch"
                      style={{ backgroundColor: backgroundColor || '#1a1a1a' }}
                    />
                  </div>
                  <span>Custom</span>
                </button>
                {materials.map((material) => (
                  <button
                    key={material.name}
                    className={`general-settings-material-item ${backgroundMaterial === material.name ? 'general-settings-material-item--selected' : ''}`}
                    onClick={() => handleBackgroundSelect(material.name)}
                    title={material.label}
                  >
                    <div className="general-settings-material-preview">
                      <img
                        src={assetsApi.getFileUrl('materials', material.name)}
                        alt={material.label}
                      />
                    </div>
                    <span>{material.label}</span>
                  </button>
                ))}
              </div>
              {isCustomColorSelected && (
                <div className="general-settings-color-input-row">
                  <input
                    type="color"
                    value={backgroundColor || '#1a1a1a'}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                  />
                  <input
                    type="text"
                    value={backgroundColor || '#1a1a1a'}
                    onChange={(e) => handleColorChange(e.target.value)}
                    placeholder="#1a1a1a"
                    maxLength={7}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="general-settings-subsection">
          <div className="general-settings-subsection-title">Background Options</div>
          <div className="general-settings-options">
            <Checkbox
              label="Random rotation"
              checked={backgroundRandomRotation}
              onChange={setBackgroundRandomRotation}
              disabled={!backgroundMaterial}
            />
            <span className="general-settings-option-hint">
              Randomly rotates background tiles to hide repetition patterns
            </span>
          </div>
        </div>
      </div>

      <div className="general-settings-section">
        <div className="general-settings-section-title">Visibility</div>
        <div className="general-settings-visibility">
          <div className="general-settings-toggle-row">
            <Toggle
              label="Default map (View mode)"
              checked={globalSettings?.defaultMapId === map.id}
              onChange={handleDefaultMapToggle}
            />
            <span className="general-settings-option-hint">
              This map will be loaded by default in view mode
            </span>
          </div>
          <div className="general-settings-toggle-row">
            <Toggle
              label="Public (visible in View mode)"
              checked={isPublic}
              onChange={setIsPublic}
            />
            <span className="general-settings-option-hint">
              This map will appear in the map selector for viewers
            </span>
          </div>
        </div>
      </div>

      {saveError && (
        <div className="general-settings-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{saveError}</span>
        </div>
      )}

      <div className="general-settings-actions">
        <button
          className="general-settings-save-btn"
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
        >
          {isSaving ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <i className="fas fa-save"></i>
              <span>Save Changes</span>
            </>
          )}
        </button>
        {hasChanges && (
          <span className="general-settings-unsaved">Unsaved changes</span>
        )}
      </div>
    </div>
  );
}

export default GeneralSettingsTab;
